import ManualGameClock from "./manual-game-clock";
import GameClock from "./game-clock";
import { migrateClockConfig } from "../../football/utils/field-config-utils";

/**
 * Factory for creating game clock instances. Selects between
 * ManualGameClock (static value, manually set) and GameClock
 * (delta-computed, ticks via wall-clock) based on the requested type.
 *
 * Old games continue to use ManualGameClock (default). Games where the
 * user opts into the running clock feature use GameClock.
 *
 * Clocks always count down: stored time is canonical remaining
 * milliseconds everywhere (plays, fragments, drives, serializers).
 * The ascending Clock Type config setting is display-only — the UI
 * converts for rendering and converts typed input back.
 */
export default class ClockFactory {
    /**
     * Create a new clock instance.
     * @param {object} options
     * @param {"manual" | "running"} [options.type="manual"]
     * @param {number} [options.limitSeconds=0] - period length in seconds (for countdown bounds)
     * @returns {Clock}
     */
    static create({ type = "manual", limitSeconds = 0 } = {}) {
        if (type === "running") {
            return new GameClock(limitSeconds * 1000);
        }
        return new ManualGameClock(limitSeconds);
    }

    /**
     * Upgrade an existing ManualGameClock instance to a GameClock,
     * preserving its current time value. Used when the user enables the
     * running clock on a game that was created with the manual clock.
     *
     * If the input is already a GameClock, returns it unchanged.
     *
     * @param {Clock} oldClock - the clock currently in the gameContext
     * @param {number} limitSeconds - period length in seconds
     * @returns {GameClock}
     */
    static upgradeToRunning(oldClock, limitSeconds) {
        // Already a GameClock? Detect by duck-typing on referenceValue —
        // robust across minification, unlike constructor.name.
        if (oldClock && "referenceValue" in oldClock) {
            return oldClock;
        }
        const currentSeconds =
            oldClock && typeof oldClock.now === "function"
                ? oldClock.now() / 1000
                : 0;
        const clock = new GameClock(limitSeconds * 1000);
        clock.setSeconds(currentSeconds);
        // Constructor default is paused. User starts via UI.
        return clock;
    }

    /**
     * Downgrade an existing GameClock instance to a ManualGameClock,
     * freezing it at the current effective time. Used when the user
     * disables the running clock mid-game.
     *
     * If the input is already a ManualGameClock, returns it unchanged.
     *
     * @param {Clock} oldClock - the clock currently in the gameContext
     * @returns {ManualGameClock}
     */
    static downgradeToManual(oldClock) {
        // Not a GameClock (same duck-typing as upgradeToRunning)? Keep it.
        if (!oldClock || !("referenceValue" in oldClock)) {
            return oldClock || new ManualGameClock(0);
        }
        const currentSeconds =
            typeof oldClock.now === "function" ? oldClock.now() / 1000 : 0;
        return new ManualGameClock(currentSeconds);
    }

    /**
     * Reconcile a context's clock with its configuration — the single
     * writer of gameContext.clockType. The config (clock section, with
     * legacy general.clockType lifted) is the only authority for the
     * running/manual choice; a missing runningClock flag means manual.
     * Idempotent: matching contexts pass through unchanged.
     *
     * Also heals clocks persisted counting up (games recorded while the
     * ascending Clock Type was wrongly applied to the clock itself):
     * their current value converts to canonical remaining time.
     *
     * Sports without clock settings (tennis) are left untouched.
     *
     * @param {GameContext} gameContext
     * @param {object} [gameConfiguration] - defaults to gameContext.gameConfig
     * @returns {GameContext} the same context, reconciled
     */
    static applyClockConfig(gameContext, gameConfiguration) {
        const config = migrateClockConfig(
            gameConfiguration ?? gameContext.gameConfig
        );
        const hasClockSettings = Boolean(config?.clock);
        if (hasClockSettings) {
            // Adopt the (migrated) clock section onto the context's own
            // config so the read-time heal — which derives from
            // gameContext.gameConfig — agrees with what was just applied.
            if (gameContext.gameConfig) {
                gameContext.gameConfig = {
                    ...gameContext.gameConfig,
                    clock: { ...config.clock }
                };
            }

            this._healUpCountingClock(gameContext.gameClock);

            const running = config.clock.runningClock === true;
            if (running) {
                const limitSeconds =
                    (config.general?.minutesByPeriod || 0) * 60;
                gameContext.gameClock = this.upgradeToRunning(
                    gameContext.gameClock,
                    limitSeconds
                );
            } else {
                gameContext.gameClock = this.downgradeToManual(
                    gameContext.gameClock
                );
                gameContext.pausedByPlaySequence = false;
            }
            gameContext.clockType = running ? "running" : "manual";
        }
        return gameContext;
    }

    /**
     * Convert an up-counting GameClock (persisted elapsed time) back to
     * the canonical countdown form (remaining time). No-op for manual
     * clocks and clocks already counting down.
     * @private
     */
    static _healUpCountingClock(clock) {
        const isUpCounting =
            clock &&
            "referenceValue" in clock &&
            clock.direction === "up" &&
            typeof clock.setSeconds === "function";
        if (isUpCounting) {
            const canonicalMs = Math.max(
                0,
                clock.timeLimitInMilliseconds - clock.getCurrentTime()
            );
            clock.direction = "down";
            clock.setSeconds(canonicalMs / 1000);
        }
    }
}
